import { linkOp, reconcileDependencies, opBody } from './dependencies';
import { protectedSnapshot } from './protectedSnapshot';
import type {
  Heat,
  ObjectionType,
  Outcome,
  PlaybookVariant,
  SpokeWith,
  TouchChannel,
} from '../../../shared/enums';
import { IN_PERSON_CHANNELS } from '../../../shared/enums';
import type { LeadField } from '../../../shared/fields';
import { LEAD_FIELDS, VISIT_FIELDS } from '../../../shared/fields';
import { personaForBusinessType } from '../../../shared/playbook';
import type {
  Feature,
  FeatureRequest,
  Lead,
  LeadSignal,
  Objection,
  PlaybookMessage,
  SyncSnapshot,
  Visit,
} from '../../../shared/types';
import type { CrmDB, OutboxMethod } from './db';
import { getDb } from './db';
import { EMPTY_LOCATION, normalizeLead, restoreManualPair, invalidateLocation } from './leadLocation';
import { buildOp } from './outbox';
import type { EnqueueInput } from './outbox';
import type { IDBPTransaction } from 'idb';

/**
 * Repository over the local IndexedDB mirror (D3/D4). Every read is local. Every write applies the
 * mirror mutation AND queues its outbox ops in ONE transaction, so a concurrent sync can never see
 * the mirror changed without the outbox knowing (or vice versa). `storeEvents` fires `change`
 * (data) and `write` (something new to sync) so views re-render and the sync engine kicks.
 */
export const storeEvents = new EventTarget();
const emit = (type: 'change' | 'write') => storeEvents.dispatchEvent(new Event(type));

export const onStoreChange = (cb: () => void): (() => void) => {
  storeEvents.addEventListener('change', cb);
  return () => storeEvents.removeEventListener('change', cb);
};

const nowIso = () => new Date().toISOString();
const uuid = () => crypto.randomUUID();

const stripUndefined = <T extends object>(o: T): Partial<T> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;

const pick = <T extends object>(o: T, keys: readonly string[]): Record<string, unknown> =>
  Object.fromEntries(Object.entries(o).filter(([k, v]) => keys.includes(k) && v !== undefined));

const ALL_STORES = ['leads', 'visits', 'objections', 'features', 'feature_requests', 'outbox'] as const;
type WriteTx = IDBPTransaction<
  CrmDB,
  ['leads', 'visits', 'objections', 'features', 'feature_requests', 'outbox'],
  'readwrite'
>;

/** The snapshot's stores: the writable ones plus the two the server owns — the message library
 *  (D88) and each lead's provenance (D96). */
const SNAPSHOT_STORES = [...ALL_STORES, 'playbook', 'lead_signals'] as const;
type SnapshotTx = IDBPTransaction<
  CrmDB,
  ['leads', 'visits', 'objections', 'features', 'feature_requests', 'outbox', 'playbook', 'lead_signals'],
  'readwrite'
>;

const applyWrite = async (ops: EnqueueInput[], mutate: (tx: WriteTx) => Promise<void>): Promise<void> => {
  const db = await getDb();
  const tx = db.transaction(ALL_STORES, 'readwrite') as WriteTx;
  await mutate(tx);
  const queue = tx.objectStore('outbox');
  for (const input of ops) {
    const all = await queue.getAll();
    const id = input.path.match(/^\/api\/leads\/([^/]+)$/)?.[1];
    const parent = all.find(o => ((o.path === '/api/leads' && o.method === 'POST' && opBody(o).id === id) || (o.path === input.path && o.method === 'PATCH')) && o.park_reason === 'conflict');
    if (input.method === 'PATCH' && parent) {
      const body = { ...opBody(parent), ...(input.body as object) };
      const changed = (body as { place_id?: unknown }).place_id !== opBody(parent).place_id;
      await queue.put({ ...parent, body, ...(changed ? { status: 'pending' as const, park_reason: undefined, existing_lead_id: undefined, last_error: undefined } : {}) });
    } else await queue.add(linkOp(buildOp(input), all));
  }
  await reconcileDependencies(queue);
  await tx.done;
  emit('change');
  emit('write');
};

// ---------------------------------------------------------------- reads

export interface Snapshot {
  leads: Lead[];
  visits: Visit[];
  objections: Objection[];
}

export const getSnapshot = async (): Promise<Snapshot> => {
  const db = await getDb();
  const tx = db.transaction(['leads', 'visits', 'objections']);
  const [leads, visits, objections] = await Promise.all([
    tx.objectStore('leads').getAll(),
    tx.objectStore('visits').getAll(),
    tx.objectStore('objections').getAll(),
  ]);
  return { leads: leads.map(normalizeLead), visits, objections };
};

export const listLeads = async (): Promise<Lead[]> => (await (await getDb()).getAll('leads')).map(normalizeLead);
export const getLead = async (id: string): Promise<Lead | undefined> => {
  const lead = await (await getDb()).get('leads', id);
  return lead ? normalizeLead(lead) : undefined;
};

export interface LeadDetail {
  lead: Lead;
  visits: Visit[];
  objections: Objection[];
  /** The public records this business was found in, newest filing first (D96). */
  signals: LeadSignal[];
}

export const getLeadDetail = async (id: string): Promise<LeadDetail | undefined> => {
  const db = await getDb();
  const lead = await db.get('leads', id);
  if (!lead) return undefined;
  const [visits, objections, signals] = await Promise.all([
    db.getAllFromIndex('visits', 'by_lead', id),
    db.getAllFromIndex('objections', 'by_lead', id),
    db.getAllFromIndex('lead_signals', 'by_lead', id),
  ]);
  const sorted = [...visits].sort((a, b) =>
    a.visited_at < b.visited_at ? 1 : a.visited_at > b.visited_at ? -1 : 0,
  );
  return { lead: normalizeLead(lead), visits: sorted, objections, signals: sortProvenance(signals) };
};

/** Newest filing first, the same order the API sends. */
export const sortProvenance = (rows: readonly LeadSignal[]): LeadSignal[] =>
  [...rows].sort(
    (a, b) =>
      b.filed_on.localeCompare(a.filed_on) ||
      b.created_at.localeCompare(a.created_at) ||
      a.id.localeCompare(b.id),
  );

/**
 * Replace the mirror when no pending op exists, preserving failed/parked local rows.
 * The pending check runs inside the same transaction as the replacement, so a local write (which
 * also commits mirror+outbox atomically) can never slip between check and replace and be wiped.
 * Returns false when skipped; the sync engine then re-runs (flush → pull) instead.
 */
export const replaceMirrorIfIdle = async (snap: SyncSnapshot): Promise<boolean> => {
  const db = await getDb();
  const tx = db.transaction(SNAPSHOT_STORES, 'readwrite') as SnapshotTx;
  const pending = await tx.objectStore('outbox').index('by_status').count('pending');
  if (pending > 0) {
    await tx.done;
    return false;
  }
  const ops = await tx.objectStore('outbox').getAll();
  if (ops.length) snap = protectedSnapshot(snap, {
    leads: await tx.objectStore('leads').getAll(), visits: await tx.objectStore('visits').getAll(),
    objections: await tx.objectStore('objections').getAll(), features: await tx.objectStore('features').getAll(),
    feature_requests: await tx.objectStore('feature_requests').getAll(),
  }, ops);
  const leads = tx.objectStore('leads');
  const visits = tx.objectStore('visits');
  const objections = tx.objectStore('objections');
  const features = tx.objectStore('features');
  const requests = tx.objectStore('feature_requests');
  const playbook = tx.objectStore('playbook');
  const provenance = tx.objectStore('lead_signals');
  await Promise.all([leads.clear(), visits.clear(), objections.clear(), features.clear(), requests.clear()]);
  // An older API sends no provenance: leave what the device has rather than emptying the block.
  if (snap.lead_signals) await provenance.clear();
  // An older API sends no playbook: the copy already on the device stays rather than emptying the
  // brief's drafts (the same rule the day routes follow, D84).
  if (snap.playbook) await playbook.clear();
  await Promise.all([
    ...snap.leads.map((l) => leads.put(normalizeLead(l))),
    ...snap.visits.map((v) => visits.put(v)),
    ...snap.objections.map((o) => objections.put(o)),
    ...(snap.features ?? []).map((f) => features.put(f)),
    ...(snap.feature_requests ?? []).map((r) => requests.put(r)),
    ...(snap.playbook ?? []).map((m) => playbook.put(m)),
    ...(snap.lead_signals ?? []).map((s) => provenance.put(s)),
  ]);
  await tx.done;
  emit('change');
  return true;
};

/** The message library as the last sync left it (D88). Empty until the first sync from a server
 *  that carries it — the brief then says so instead of showing a blank draft. */
export const listPlaybook = async (): Promise<PlaybookMessage[]> => (await getDb()).getAll('playbook');

const PLAYBOOK_STAMP = 'playbook_stamp';

/** Which copy of the library this device holds; sent with the sync so the rows are only resent
 *  when they actually changed. Null until the first snapshot that carried them. */
export const playbookStamp = async (): Promise<string | null> => {
  const row = await (await getDb()).get('meta', PLAYBOOK_STAMP);
  return typeof row?.value === 'string' ? row.value : null;
};

export const keepPlaybookStamp = async (stamp: string | null): Promise<void> => {
  const db = await getDb();
  if (stamp === null) await db.delete('meta', PLAYBOOK_STAMP);
  else await db.put('meta', { key: PLAYBOOK_STAMP, value: stamp });
};

// ---------------------------------------------------------------- leads

export type LeadInput = Partial<Pick<Lead, LeadField>> & {
  business_name: string;
  id?: string;
  lat?: number;
  lng?: number;
};

const EMPTY_LEAD: Omit<Lead, 'id' | 'business_name' | 'created_at' | 'updated_at'> = {
  ...EMPTY_LOCATION,
  business_type: 'other',
  address: null,
  place_id: null,
  neighborhood: null,
  owner_name: null,
  owner_role: null,
  owner_language: 'en',
  phone: null,
  email: null,
  instagram: null,
  current_pos: 'unknown',
  pos_monthly_cost: null,
  contract_end_date: null,
  etf_amount: null,
  stage: 'new',
  heat: 'warm',
  best_visit_window: null,
  notes: null,
  meeting_on: null,
  demo_on: null,
  application_on: null,
  activation_on: null,
  gpv_usd: null,
  gpv_as_of: null,
};

export const createLead = async (input: LeadInput): Promise<Lead> => {
  const now = nowIso();
  const lead: Lead = {
    ...EMPTY_LEAD,
    ...stripUndefined(input),
    ...(input.lat !== undefined && input.lng !== undefined ? { lat: input.lat, lng: input.lng, coordinate_source: 'manual' as const, coordinate_at: now, location_status: 'resolved' as const, location_version: 1 } : {}),
    business_name: input.business_name.trim(),
    id: input.id ?? uuid(),
    created_at: now,
    updated_at: now,
  };
  await applyWrite(
    [
      {
        method: 'POST',
        path: '/api/leads',
        body: { id: lead.id, created_at: lead.created_at, ...pick(lead, LEAD_FIELDS), ...restoreManualPair(lead) },
        label: `New lead · ${lead.business_name}`,
      },
    ],
    async (tx) => {
      await tx.objectStore('leads').put(lead);
    },
  );
  return lead;
};

export type LeadPatch = Partial<Pick<Lead, LeadField>>;

export const updateLead = async (id: string, patch: LeadPatch): Promise<Lead> => {
  const db = await getDb();
  const current = await db.get('leads', id);
  if (!current) throw new Error(`Lead ${id} not found locally`);
  const clean = pick(stripUndefined(patch), LEAD_FIELDS);
  if (Object.keys(clean).length === 0) return current;
  const next: Lead = { ...invalidateLocation(current, clean as LeadPatch), ...(clean as LeadPatch), updated_at: nowIso() };
  await applyWrite(
    [{ method: 'PATCH', path: `/api/leads/${id}`, body: clean, label: `Update · ${next.business_name}` }],
    async (tx) => {
      await tx.objectStore('leads').put(next);
    },
  );
  return next;
};

/** What a delete removed, kept so the "tap to undo" toast can put it back. */
export interface LeadArchive {
  lead: Lead;
  visits: Visit[];
  objections: Objection[];
  requests: FeatureRequest[];
}

export const deleteLead = async (id: string): Promise<LeadArchive | undefined> => {
  const db = await getDb();
  const lead = await db.get('leads', id);
  if (!lead) return undefined; // already gone (a double tap): nothing to queue, nothing to undo
  let archive: LeadArchive | undefined;
  await applyWrite(
    [{ method: 'DELETE', path: `/api/leads/${id}`, label: `Delete · ${lead.business_name}` }],
    async (tx) => {
      const [current, visits, objections, requests] = await Promise.all([
        tx.objectStore('leads').get(id),
        tx.objectStore('visits').index('by_lead').getAll(id),
        tx.objectStore('objections').index('by_lead').getAll(id),
        tx.objectStore('feature_requests').index('by_lead').getAll(id),
      ]);
      if (current) archive = { lead: current, visits, objections, requests };
      await Promise.all([
        tx.objectStore('leads').delete(id),
        ...visits.map((v) => tx.objectStore('visits').delete(v.id)),
        ...objections.map((o) => tx.objectStore('objections').delete(o.id)),
        ...requests.map((r) => tx.objectStore('feature_requests').delete(r.id)),
      ]);
    },
  );
  return archive;
};

/** Drop a queued op that has not been sent yet (undo of a delete that never left the phone). */
const dropPendingOp = async (tx: WriteTx, method: OutboxMethod, path: string): Promise<void> => {
  const pending = await tx.objectStore('outbox').index('by_status').getAll('pending');
  const op = pending.find((o) => o.method === method && o.path === path);
  if (op?.seq !== undefined) await tx.objectStore('outbox').delete(op.seq);
};

/** Undo for deleteLead: the rows go back into the mirror and a DELETE still in the outbox is
 *  dropped. Idempotent re-creates (D2) are queued as well, because the DELETE may already have
 *  reached the server, even mid-flight. Email drafts the server cascaded away are not restored. */
export const restoreLead = async (a: LeadArchive): Promise<void> => {
  const name = a.lead.business_name;
  const visits = [...a.visits].sort((x, y) => (x.visited_at < y.visited_at ? -1 : 1));
  const ops: EnqueueInput[] = [
    {
      method: 'POST',
      path: '/api/leads',
      body: { id: a.lead.id, created_at: a.lead.created_at, ...pick(a.lead, LEAD_FIELDS), ...restoreManualPair(a.lead) },
      label: `Restore · ${name}`,
    },
    ...visits.map((v): EnqueueInput => ({
      method: 'POST',
      path: '/api/visits',
      body: { id: v.id, ...pick(v, VISIT_FIELDS) },
      label: `Restore visit · ${name}`,
    })),
    ...a.objections.map((o): EnqueueInput => ({
      method: 'POST',
      path: '/api/objections',
      body: { id: o.id, lead_id: o.lead_id, visit_id: o.visit_id, type: o.type, detail: o.detail },
      label: `Restore objection · ${name}`,
    })),
    ...a.requests.map((r) => requestOp(r, `Restore feature ask · ${name}`)),
  ];
  await applyWrite(ops, async (tx) => {
    await dropPendingOp(tx, 'DELETE', `/api/leads/${a.lead.id}`);
    await Promise.all([
      tx.objectStore('leads').put(a.lead),
      ...a.visits.map((v) => tx.objectStore('visits').put(v)),
      ...a.objections.map((o) => tx.objectStore('objections').put(o)),
      ...a.requests.map((r) => tx.objectStore('feature_requests').put(r)),
    ]);
  });
};

// ---------------------------------------------------------------- visits (quick log)

export interface LogVisitInput {
  lead_id: string;
  spoke_with: SpokeWith;
  outcome: Outcome;
  notes?: string | null;
  next_action?: string | null;
  next_action_date?: string | null;
  next_action_time?: string | null;
  visited_at?: string;
  /** How the touch happened (D90); a walk-in when the screen does not say. */
  channel?: TouchChannel;
  /** The message variant used, when one was (D88). The persona is read from the lead. */
  variant?: PlaybookVariant | null;
  objections?: { type: ObjectionType; detail?: string | null }[];
  /** Feature asks captured during the visit (existing id or new name) — M4. */
  features?: FeatureAsk[];
  /** Optional heat change captured on the same screen. */
  heat?: Heat;
}

export interface LogVisitResult {
  visit: Visit;
  objections: Objection[];
  lead: Lead;
}

const nullIfBlank = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim();
  return t.length ? t : null;
};

export const logVisit = async (input: LogVisitInput): Promise<LogVisitResult> => {
  const db = await getDb();
  const lead = await db.get('leads', input.lead_id);
  if (!lead) throw new Error(`Lead ${input.lead_id} not found locally`);
  const now = nowIso();
  const visit: Visit = {
    id: uuid(),
    lead_id: lead.id,
    visited_at: input.visited_at ?? now,
    spoke_with: input.spoke_with,
    outcome: input.outcome,
    notes: nullIfBlank(input.notes),
    next_action: nullIfBlank(input.next_action),
    next_action_date: input.next_action_date ?? null,
    next_action_time: input.next_action_date ? (input.next_action_time ?? null) : null,
    // D90: the persona is the lead's at the time of the touch, kept on the row for the funnel
    channel: input.channel ?? 'walk_in',
    persona: personaForBusinessType(lead.business_type),
    variant: input.variant ?? null,
  };
  const objections: Objection[] = (input.objections ?? []).map((o) => ({
    id: uuid(),
    lead_id: lead.id,
    visit_id: visit.id,
    type: o.type,
    detail: nullIfBlank(o.detail),
  }));
  // D10 mirrored locally (amended by D90: only an in-person touch is a visit); the server applies
  // the same rule on POST /api/visits.
  const inPerson = IN_PERSON_CHANNELS.includes(visit.channel);
  const leadPatch: LeadPatch = {
    ...(lead.stage === 'new' && inPerson ? { stage: 'visited' as const } : {}),
    ...(input.heat && input.heat !== lead.heat ? { heat: input.heat } : {}),
  };
  const nextLead: Lead = Object.keys(leadPatch).length ? { ...lead, ...leadPatch, updated_at: now } : lead;

  const asks = requestOps(lead, input.features ?? [], visit.id);
  const ops: EnqueueInput[] = [
    // new features FIRST: their requests (queued later) reference them server-side
    ...asks.ops.filter((o) => o.path === '/api/features'),
    {
      method: 'POST',
      path: '/api/visits',
      body: { id: visit.id, ...pick(visit, VISIT_FIELDS) },
      label: `Log visit · ${lead.business_name}`,
    },
    ...objections.map((o): EnqueueInput => ({
      method: 'POST',
      path: '/api/objections',
      body: { id: o.id, lead_id: o.lead_id, visit_id: o.visit_id, type: o.type, detail: o.detail },
      label: `Objection · ${lead.business_name}`,
    })),
    ...asks.ops.filter((o) => o.path === '/api/feature-requests'),
    ...(leadPatch.heat
      ? [
          {
            method: 'PATCH',
            path: `/api/leads/${lead.id}`,
            body: { heat: leadPatch.heat },
            label: `Heat · ${lead.business_name}`,
          } as EnqueueInput,
        ]
      : []),
  ];

  await applyWrite(ops, async (tx) => {
    await Promise.all([
      tx.objectStore('visits').put(visit),
      ...objections.map((o) => tx.objectStore('objections').put(o)),
      ...asks.features.map((f) => tx.objectStore('features').put(f)),
      ...asks.requests.map((r) => tx.objectStore('feature_requests').put(r)),
      nextLead !== lead ? tx.objectStore('leads').put(nextLead) : Promise.resolve(),
    ]);
  });
  return { visit, objections, lead: nextLead };
};

// ---------------------------------------------------------------- watchlist (M4, D35)

export interface WatchlistData {
  features: Feature[];
  requests: FeatureRequest[];
  leads: Lead[];
  visits: Visit[];
}

export const getWatchlistData = async (): Promise<WatchlistData> => {
  const db = await getDb();
  const tx = db.transaction(['features', 'feature_requests', 'leads', 'visits']);
  const [features, requests, leads, visits] = await Promise.all([
    tx.objectStore('features').getAll(),
    tx.objectStore('feature_requests').getAll(),
    tx.objectStore('leads').getAll(),
    tx.objectStore('visits').getAll(),
  ]);
  return { features, requests, leads, visits };
};

export const listFeatures = async (): Promise<Feature[]> => (await getDb()).getAll('features');

export const createFeature = async (name: string): Promise<Feature> => {
  const feature: Feature = { id: uuid(), name: name.trim(), status: 'missing', launched_at: null };
  await applyWrite(
    [
      {
        method: 'POST',
        path: '/api/features',
        body: { id: feature.id, name: feature.name },
        label: `Track feature · ${feature.name}`,
      },
    ],
    async (tx) => {
      await tx.objectStore('features').put(feature);
    },
  );
  return feature;
};

export interface FeatureArchive {
  feature: Feature;
  requests: FeatureRequest[];
}

export const deleteFeature = async (id: string): Promise<FeatureArchive | undefined> => {
  const db = await getDb();
  const feature = await db.get('features', id);
  if (!feature) return undefined; // already gone (a double tap): nothing to queue, nothing to undo
  let archive: FeatureArchive | undefined;
  await applyWrite(
    [{ method: 'DELETE', path: `/api/features/${id}`, label: `Untrack · ${feature.name}` }],
    async (tx) => {
      const [current, requests] = await Promise.all([
        tx.objectStore('features').get(id),
        tx.objectStore('feature_requests').index('by_feature').getAll(id),
      ]);
      if (current) archive = { feature: current, requests };
      await Promise.all([
        tx.objectStore('features').delete(id),
        ...requests.map((r) => tx.objectStore('feature_requests').delete(r.id)),
      ]);
    },
  );
  return archive;
};

/** Undo for deleteFeature, same approach as restoreLead. The API re-creates a feature as "missing",
 *  so a launched one is launched again — idempotent per lead, its drafts survived the delete (D34). */
export const restoreFeature = async (a: FeatureArchive): Promise<void> => {
  const relaunch: EnqueueInput[] =
    a.feature.status === 'launched'
      ? [
          {
            method: 'POST',
            path: `/api/features/${a.feature.id}/launch`,
            label: `Restore launch · ${a.feature.name}`,
          },
        ]
      : [];
  const ops: EnqueueInput[] = [
    {
      method: 'POST',
      path: '/api/features',
      body: { id: a.feature.id, name: a.feature.name },
      label: `Restore feature · ${a.feature.name}`,
    },
    ...a.requests.map((r) => requestOp(r, `Restore feature ask · ${a.feature.name}`)),
    ...relaunch,
  ];
  await applyWrite(ops, async (tx) => {
    await dropPendingOp(tx, 'DELETE', `/api/features/${a.feature.id}`);
    await Promise.all([
      tx.objectStore('features').put(a.feature),
      ...a.requests.map((r) => tx.objectStore('feature_requests').put(r)),
    ]);
  });
};

const requestOp = (r: FeatureRequest, label: string): EnqueueInput => ({
  method: 'POST',
  path: '/api/feature-requests',
  body: { id: r.id, feature_id: r.feature_id, lead_id: r.lead_id, visit_id: r.visit_id, notes: r.notes },
  label,
});

export interface FeatureAsk {
  /** Existing feature id — or absent with `name` set to create the feature on the spot. */
  feature_id?: string;
  name?: string;
  notes?: string | null;
}

const requestOps = (
  lead: Lead,
  asks: FeatureAsk[],
  visit_id: string | null,
): { features: Feature[]; requests: FeatureRequest[]; ops: EnqueueInput[] } => {
  const features: Feature[] = [];
  const requests: FeatureRequest[] = [];
  const ops: EnqueueInput[] = [];
  for (const ask of asks) {
    let featureId = ask.feature_id;
    if (!featureId) {
      const name = (ask.name ?? '').trim();
      if (!name) continue;
      const feature: Feature = { id: uuid(), name, status: 'missing', launched_at: null };
      features.push(feature);
      featureId = feature.id;
      ops.push({
        method: 'POST',
        path: '/api/features',
        body: { id: feature.id, name: feature.name },
        label: `Track feature · ${feature.name}`,
      });
    }
    const request: FeatureRequest = {
      id: uuid(),
      feature_id: featureId,
      lead_id: lead.id,
      visit_id,
      notes: (ask.notes ?? '').trim() || null,
    };
    requests.push(request);
    ops.push(requestOp(request, `Feature ask · ${lead.business_name}`));
  }
  return { features, requests, ops };
};

/** Record feature asks from the lead detail screen (no visit attached). */
export const requestFeatures = async (lead_id: string, asks: FeatureAsk[]): Promise<void> => {
  const db = await getDb();
  const lead = await db.get('leads', lead_id);
  if (!lead) throw new Error(`Lead ${lead_id} not found locally`);
  const { features, requests, ops } = requestOps(lead, asks, null);
  if (ops.length === 0) return;
  await applyWrite(ops, async (tx) => {
    await Promise.all([
      ...features.map((f) => tx.objectStore('features').put(f)),
      ...requests.map((r) => tx.objectStore('feature_requests').put(r)),
    ]);
  });
};
